import { Box, Paper, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { SystemStackedBar } from "../../../../components/UIToolkit/DataViz/SystemStackedBar/SystemStackedBar";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { largestReviewBucket, REVIEW_BUCKET_HOURS } from "../../network/networkMath";
import { sectionCardSx } from "../../PredictionTab/colors";
import type { OpenAuditDestination } from "../auditSubTabs";
import type { BuildingSummaryRow } from "./BuildingsSummaryTable";
import { REVIEW_BUCKET_VIEWS, reviewBucketView } from "./reviewBuckets";

/** How many buildings the card ranks — enough to plan a morning's review without becoming the table below. */
const WHERE_TO_START_COUNT = 6;
/** The building name's share of a row; the bar takes the rest. */
const NAME_SHARE = "32%";

type Props = {
	rows: readonly BuildingSummaryRow[];
	formatHours: (hours: number) => string;
	onOpen: OpenAuditDestination;
};

/** The buildings with the most hours to review, each opening its biggest problem first. */
export const WhereToStartCard: FC<Props> = ({ rows, formatHours, onOpen }) => {
	const { t } = useTranslation();
	const ranked = [...rows]
		.filter((row) => row.hoursToReview > 0)
		.sort((first, second) => second.hoursToReview - first.hoursToReview)
		.slice(0, WHERE_TO_START_COUNT);
	// Every bar shares one scale — the largest unrounded bar — so their lengths compare.
	const scale = Math.max(...ranked.map((row) => REVIEW_BUCKET_VIEWS.reduce((total, view) => total + REVIEW_BUCKET_HOURS[view.bucket](row), 0)));
	return (
		<Paper elevation={0} sx={(theme) => ({ ...sectionCardSx(theme), p: 2.5, flex: 1, minWidth: 0 })} data-testid="pbj-network-summary-where-to-start">
			<Typography variant="overline" color="text.secondary">
				{t("pbj_model.network.summary.where_to_start.title")}
			</Typography>
			{ranked.length === 0 ? (
				<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="pbj-network-summary-where-to-start-empty">
					{t("pbj_model.network.summary.where_to_start.empty")}
				</Typography>
			) : (
				<Stack spacing={0.25} sx={{ mt: 0.5 }}>
					{ranked.map((row) => (
						<SystemButton
							key={row.departmentId}
							buttonVariant="borderless"
							size="small"
							onClick={() => onOpen(reviewBucketView(largestReviewBucket(row)).destination, row.departmentId)}
							aria-label={t("pbj_model.network.summary.where_to_start.open", { building: row.buildingName, hours: formatHours(row.hoursToReview) })}
							data-testid={`pbj-network-summary-start-${row.departmentId}`}
							// Responsive form on purpose: SystemButton sets its own width per breakpoint, which a plain
							// `width` can't outrank.
							sx={{ width: { xs: "100%", sm: "100%" }, minWidth: 0 }}
						>
							<Stack direction="row" spacing={1.5} sx={{ alignItems: "center", width: "100%" }}>
								<Typography variant="body2" noWrap sx={{ flex: `0 0 ${NAME_SHARE}`, textAlign: "start" }}>
									{row.buildingName}
								</Typography>
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<SystemStackedBar
										segments={REVIEW_BUCKET_VIEWS.map((view) => ({ id: view.bucket, value: REVIEW_BUCKET_HOURS[view.bucket](row), color: view.color }))}
										max={scale}
										ariaLabel={t("pbj_model.network.summary.hours_unit", { hours: formatHours(row.hoursToReview) })}
									/>
								</Box>
								<Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
									{t("pbj_model.network.summary.hours_unit", { hours: formatHours(row.hoursToReview) })}
								</Typography>
							</Stack>
						</SystemButton>
					))}
				</Stack>
			)}
			<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
				{t("pbj_model.network.summary.where_to_start.hint")}
			</Typography>
		</Paper>
	);
};
